import { useCallback, useMemo } from 'react';
import { useUsersContext } from './context';
import { ACTIONS } from './reducer';
import { apiCaller, APIError, createSearchCriteria, createPageable } from 'utils/axios';
import { API_ENDPOINTS } from '../apiEndpoints';
import Swal from 'sweetalert2';
import debounce from 'lodash/debounce';
import secureLocalStorage from 'react-secure-storage';

export const useUsers = () => {
    const [state, dispatch] = useUsersContext();

    const setLoading = useCallback(
        (loading) => {
            dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
        },
        [dispatch]
    );

    const showSuccessMessage = useCallback((message) => {
        Swal.fire({
            title: 'Success',
            text: message,
            icon: 'success'
        });
    }, []);

    const showErrorMessage = useCallback(
        (error) => {
            let errorMessage = 'An unexpected error occurred';
            let errors = null;

            if (error instanceof APIError) {
                errorMessage = error.message;
                errors = error.errors;
            }

            Swal.fire({
                title: 'Error',
                text: errorMessage,
                icon: 'error'
            });

            dispatch({ type: ACTIONS.SET_ERROR, payload: { message: errorMessage, errors } });
        },
        [dispatch]
    );

    const setFilter = useCallback(
        (filterData) => {
            dispatch({ type: ACTIONS.SET_FILTER, payload: filterData });
        },
        [dispatch]
    );

    const setSort = useCallback(
        (sortData) => {
            dispatch({ type: ACTIONS.SET_SORT, payload: sortData });
        },
        [dispatch]
    );

    const setPage = useCallback(
        (pageData) => {
            dispatch({ type: ACTIONS.SET_PAGE, payload: pageData });
        },
        [dispatch]
    );

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const { filter, pagination, sort } = state;

            const searchCriteria = Object.entries(filter)
                .filter(([_, value]) => value !== null && value !== undefined)
                .map(([key, value]) => {
                    if (key === 'dateRange') {
                        return [
                            createSearchCriteria({
                                key: 'startDate',
                                value: value.start,
                                operation: 'GREATER_THAN_EQUAL'
                            }),
                            createSearchCriteria({
                                key: 'endDate',
                                value: value.end,
                                operation: 'LESS_THAN_EQUAL'
                            })
                        ];
                    }
                    return createSearchCriteria({
                        key,
                        value,
                        operation: 'EQUAL'
                    });
                })
                .flat();

            const pageable = createPageable({
                page: pagination.page,
                size: pagination.size,
                sort: [{ field: sort.field, direction: sort.direction }]
            });

            const data = await apiCaller.search(API_ENDPOINTS.USERS, searchCriteria, pageable);
            dispatch({ type: ACTIONS.SET_USERS, payload: data });
            return data;
        } catch (error) {
            showErrorMessage(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [setLoading, showErrorMessage, dispatch, state]);

    const searchUsers = useCallback(
        async (searchParams = {}, pageParams = {}) => {
            try {
                setLoading(true);

                const searchCriteria = Object.entries(searchParams).map(([key, value]) =>
                    createSearchCriteria({
                        key,
                        value: value.value,
                        operation: value.operation || 'EQUAL',
                        orPredicate: value.orPredicate || false
                    })
                );

                const pageable = createPageable({
                    page: pageParams.page || 0,
                    size: pageParams.size || 10,
                    sort: pageParams.sort || []
                });

                const data = await apiCaller.search(API_ENDPOINTS.USERS, searchCriteria, pageable);
                dispatch({ type: ACTIONS.SET_USERS, payload: data });
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showErrorMessage, dispatch]
    );

    const createUser = useCallback(
        async (userData) => {
            try {
                setLoading(true);

                // Ensure the userData matches the required API structure
                const payload = {
                    email: userData.email,
                    employeeNumber: userData.employeeNumber,
                    firstName: userData.firstName,
                    lastName: userData.lastName,
                    employeeTitle: userData.employeeTitle,
                    password: userData.password,
                    department: userData.department
                };

                const data = await apiCaller.post(API_ENDPOINTS.USERS, payload);
                dispatch({ type: ACTIONS.ADD_USER, payload: data });

                showSuccessMessage('User created successfully');
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, showErrorMessage, dispatch]
    );

    const updateUser = useCallback(
        async (id, userData) => {
            try {
                setLoading(true);

                const data = await apiCaller.put(`${API_ENDPOINTS.USERS}/${id}`, userData);
                dispatch({ type: ACTIONS.UPDATE_USER, payload: { id, data } });

                showSuccessMessage('User updated successfully');
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, showErrorMessage, dispatch]
    );

    const deleteUser = useCallback(
        async (id) => {
            try {
                setLoading(true);
                await apiCaller.delete(`${API_ENDPOINTS.USERS}/${id}`);
                dispatch({ type: ACTIONS.DELETE_USER, payload: id });

                showSuccessMessage('User deleted successfully');
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, showErrorMessage, dispatch]
    );

    const getUserById = useCallback(
        async (id) => {
            try {
                setLoading(true);
                const data = await apiCaller.get(`${API_ENDPOINTS.USERS}/${id}`);
                dispatch({ type: ACTIONS.SET_SELECTED_USER, payload: data });
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showErrorMessage, dispatch]
    );

    const addComment = useCallback(
        async (userId, comment) => {
            try {
                setLoading(true);
                const data = await apiCaller.post(`${API_ENDPOINTS.USERS}/${userId}/comments`, comment);
                dispatch({
                    type: ACTIONS.ADD_USER_COMMENT,
                    payload: data
                });
                showSuccessMessage('Comment added successfully');
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, showErrorMessage, dispatch]
    );

    const updateUserStatus = useCallback(
        async (userId, status) => {
            try {
                setLoading(true);
                const data = await apiCaller.put(`${API_ENDPOINTS.USERS}/${userId}/status`, { status });
                dispatch({
                    type: ACTIONS.SET_USER_STATUS,
                    payload: { id: userId, status }
                });
                showSuccessMessage(`User status updated to ${status}`);
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, showErrorMessage, dispatch]
    );

    const updateApprover = useCallback(
        async (userId, approverData) => {
            try {
                setLoading(true);
                const data = await apiCaller.put(`${API_ENDPOINTS.USERS}/${userId}/approvers/${approverData.approverId}`, approverData);
                dispatch({
                    type: ACTIONS.UPDATE_USER_APPROVER,
                    payload: data
                });
                showSuccessMessage('Approver updated successfully');
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, showErrorMessage, dispatch]
    );

    const updateAttachments = useCallback(
        async (userId, files) => {
            try {
                setLoading(true);
                const formData = new FormData();
                files.forEach((file) => formData.append('files', file));

                const data = await apiCaller.put(`${API_ENDPOINTS.USERS}/${userId}/attachments`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                dispatch({
                    type: ACTIONS.UPDATE_USER_ATTACHMENTS,
                    payload: data
                });
                showSuccessMessage('Attachments updated successfully');
                return data;
            } catch (error) {
                showErrorMessage(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, showErrorMessage, dispatch]
    );

    const clearError = useCallback(() => {
        dispatch({ type: ACTIONS.CLEAR_ERROR });
    }, [dispatch]);

    const searchUsersByTerm = useCallback(
        debounce(async (searchTerm) => {
            if (!searchTerm || searchTerm.length < 2) {
                dispatch({ type: ACTIONS.SET_SEARCH_RESULTS, payload: [] });
                return;
            }

            try {
                setLoading(true);
                const searchCriteria = [
                    // createSearchCriteria({
                    //     key: 'employeeNumber',
                    //     value: searchTerm,
                    //     operation: 'LIKE_START',
                    //     orPredicate: true
                    // }),
                    createSearchCriteria({
                        key: 'email',
                        value: searchTerm,
                        operation: 'LIKE_START',
                        orPredicate: true
                    })
                ];

                const pageable = createPageable({
                    page: 0,
                    size: 10
                });

                const data = await apiCaller.search(`${API_ENDPOINTS.USERS}`, searchCriteria, pageable);
                dispatch({ type: ACTIONS.SET_SEARCH_RESULTS, payload: data.content || [] });
            } catch (error) {
                showErrorMessage(error);
                dispatch({ type: ACTIONS.SET_SEARCH_RESULTS, payload: [] });
            } finally {
                setLoading(false);
            }
        }, 300),
        [dispatch, setLoading, showErrorMessage]
    );

    const getUserRoles = useCallback(async () => {
        try {
            setLoading(true);
            const data = await apiCaller.get(API_ENDPOINTS.ROLES);
            dispatch({
                type: ACTIONS.SET_USER_ROLES,
                payload: Array.isArray(data) ? data : []
            });
            return data;
        } catch (error) {
            showErrorMessage(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [setLoading, showErrorMessage, dispatch]);

    const getCurrentUserRole = useCallback(() => {
        try {
            const userData = secureLocalStorage.getItem('user_roles');
            if (userData) {
                const parsedData = JSON.parse(userData);
                if (parsedData && parsedData[0]?.role?.name) {
                    return parsedData[0].role.name;
                }
            }
            return null;
        } catch (error) {
            console.error('Error fetching user role:', error);
            return null;
        }
    }, []);

    const getCurrentUserId = useCallback(() => {
        try {
            const userData = secureLocalStorage.getItem('user_roles');
            if (userData) {
                const userId = JSON.parse(userData)[0]?.user?.id;
                return userId || null;
            }
            return null;
        } catch (error) {
            console.error('Error fetching user ID:', error);
            return null;
        }
    }, []);

    const getCurrentUserDemoData = useCallback(() => {
        try {
            const userData = secureLocalStorage.getItem('user_roles');
            if (userData) {
                const payload = JSON.parse(userData);
                return payload || null;
            }
            return null;
        } catch (error) {
            console.error('Error fetching user ID:', error);
            return null;
        }
    }, []);

    return useMemo(
        () => ({
            // State
            users: state.users,
            selectedUser: state.selectedUser,
            loading: state.loading,
            error: state.error,
            filter: state.filter,
            pagination: state.pagination,
            sort: state.sort,
            roles: state.roles || [],

            // List Operations
            setFilter,
            setSort,
            setPage,
            fetchUsers,
            searchUsers,
            getUserRoles,
            getCurrentUserRole,
            getCurrentUserId,
            getCurrentUserDemoData,

            // CRUD Operations
            createUser,
            updateUser,
            deleteUser,
            getUserById,

            // Additional Operations
            addComment,
            updateUserStatus,
            updateApprover,
            updateAttachments,
            clearError,

            searchUsersByTerm,
            searchResults: Array.isArray(state.searchResults) ? state.searchResults : []
        }),
        [
            state,
            setFilter,
            setSort,
            setPage,
            fetchUsers,
            searchUsers,
            createUser,
            updateUser,
            deleteUser,
            getUserById,
            addComment,
            updateUserStatus,
            updateApprover,
            updateAttachments,
            clearError,
            searchUsersByTerm,
            getCurrentUserRole,
            getUserRoles,
            getCurrentUserId,
            getCurrentUserDemoData
        ]
    );
};

export default useUsers;
